import React, { useRef, useEffect, useState } from "react";
import MessageLayout from "../messageLayout";
import type { MessageListProps } from "@/types/chat.types";

/**
 * MessageList Component
 *
 * Responsibilities:
 * - Display list of messages
 * - Handle scrolling and scroll-to-bottom logic
 * - Manage pagination/infinite scroll
 * - Pass message-specific handlers to MessageLayout
 * - Highlight messages when navigating
 *
 * Parent: ChatArea
 * Children: MessageLayout (one per message)
 */
const MessageList: React.FC<MessageListProps> = ({
  messages,
  currentUser,
  isLoading = false,
  onLoadMore,
  hasMore = false,
  eventHandlers,
  repliedMessage,
  currentRoom,
}) => {
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const [highlightedMessageId, setHighlightedMessageId] = useState<string | null>(null);
  const [shouldScrollToBottom, setShouldScrollToBottom] = useState(true);
  const prevMessageCountRef = useRef(messages.length);
  const prevScrollHeightRef = useRef(0);

  /**
   * Handle scroll position when messages change
   */
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const prevCount = prevMessageCountRef.current;
    const currentCount = messages.length;

    // Messages were prepended (pagination loaded older messages)
    if (currentCount > prevCount && !shouldScrollToBottom) {
      const prevScrollHeight = prevScrollHeightRef.current;
      const newScrollHeight = container.scrollHeight;
      const heightDiff = newScrollHeight - prevScrollHeight;

      // Maintain scroll position by adjusting for new content height
      if (heightDiff > 0) {
        container.scrollTop = container.scrollTop + heightDiff;
        console.log(`📌 Adjusted scroll position by ${heightDiff}px to maintain view`);
      }
    }
    // New messages arrived at bottom (should scroll to bottom)
    else if (shouldScrollToBottom && currentCount > prevCount) {
      container.scrollTop = container.scrollHeight;
    }

    prevMessageCountRef.current = currentCount;
  }, [messages.length, shouldScrollToBottom]);

  /**
   * Track if user is near bottom to auto-scroll new messages
   */
  const handleScroll = () => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;
    setShouldScrollToBottom(isNearBottom);

    // Load more messages when scrolling to top
    if (scrollTop < 50 && hasMore && onLoadMore && !isLoading) {
      console.log('🔄 Triggering load more (scrollTop:', scrollTop, ')');
      // Save scroll height before loading more
      prevScrollHeightRef.current = scrollHeight;
      onLoadMore();
    }
  };

  /**
   * Navigate to a specific message and highlight it
   */
  const handleNavigateToMessage = (messageId: string) => {
    const messageElement = document.getElementById(`message-${messageId}`);
    if (messageElement) {
      messageElement.scrollIntoView({ behavior: "smooth", block: "center" });
      setHighlightedMessageId(messageId);

      // Remove highlight after 3 seconds
      setTimeout(() => {
        setHighlightedMessageId(null);
      }, 3000);
    }
  };

  /**
   * Enhanced event handlers with navigation support
   */
  const enhancedHandlers = {
    ...eventHandlers,
    onNavigateToMessage: handleNavigateToMessage,
  };

  return (
    <div
      ref={messagesContainerRef}
      onScroll={handleScroll}
      className="h-full overflow-y-auto px-2 sm:px-4 md:px-6 py-2 sm:py-4 pb-4 scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600 scrollbar-track-transparent"
    >
      {/* Loading indicator at top for pagination */}
      {isLoading && hasMore && (
        <div className="flex justify-center items-center py-3 mb-2">
          <div className="flex items-center gap-2 px-4 py-2 bg-purple-50 dark:bg-purple-900/20 rounded-full">
            <div className="animate-spin rounded-full h-4 w-4 border-2 border-purple-600 border-t-transparent"></div>
            <span className="text-sm text-purple-600 dark:text-purple-400 font-medium">
              Loading older messages...
            </span>
          </div>
        </div>
      )}

      {/* Messages */}
      {messages.map((message) => {
        return (
          <div key={message.messageId} id={`message-${message.messageId}`}>
            <MessageLayout
              message={message}
              currentRoom={currentRoom}
              onEditMessage={enhancedHandlers.onEdit}
              handleDeleteMessage={(messageId: string, chatroomId: string) =>
                enhancedHandlers.onDelete(messageId, chatroomId)
              }
              handleUndoMessage={(messageId: string, toName: string) =>
                enhancedHandlers.onDelete(messageId, toName)
              }
              onReply={enhancedHandlers.onReply}
              onNavigateToMessage={enhancedHandlers.onNavigateToMessage}
              isHighlighted={highlightedMessageId === message.messageId}
            />
          </div>
        );
      })}

      {/* Small spacer for visual breathing room */}
      {messages.length > 0 && <div className="h-4" aria-hidden="true" />}

      {/* Empty state */}
      {messages.length === 0 && !isLoading && (
        <div className="flex items-center justify-center h-full text-gray-500 dark:text-gray-400">
          <p>No messages yet. Start the conversation!</p>
        </div>
      )}
    </div>
  );
};

export default MessageList;
